import React from "react";
import { format, parseISO } from "date-fns";
import { Chip, Tooltip } from "@material-ui/core";

// ── Types matching the salary-per-series API response ─────────────────────────

interface AppliedRule {
  title: string;
  value: number;
  handler: "None" | "Percentage" | "Addition";
  identifier: string;
  isEditable: boolean;
  predicate?: {
    predicateType: string;
    hoursInterval?: { start: string | number; end?: string | number };
    days?: string[];
  };
}

export interface SalaryV2SeriesSlot {
  start: string;
  end: string;
  salary: number;      // øre
  groupLength: number; // half-hour units
  rulesAppied: AppliedRule[]; // note: API typo preserved
}

interface Props {
  series: SalaryV2SeriesSlot[];
  total: number; // øre
}

// ── Badge colors ──────────────────────────────────────────────────────────────

const BADGE_PALETTE = [
  { bg: "#e3f2fd", border: "#1976d2", text: "#0d47a1" },
  { bg: "#e8f5e9", border: "#388e3c", text: "#1b5e20" },
  { bg: "#fff3e0", border: "#f57c00", text: "#e65100" },
  { bg: "#f3e5f5", border: "#7b1fa2", text: "#4a148c" },
  { bg: "#fce4ec", border: "#c62828", text: "#880e4f" },
];

function chipStyle(colorIdx: number): React.CSSProperties {
  const c = BADGE_PALETTE[colorIdx % BADGE_PALETTE.length];
  return {
    background: c.bg,
    color: c.text,
    border: `1px solid ${c.border}`,
    fontWeight: 600,
  };
}


// ── Formatting helpers ────────────────────────────────────────────────────────

function formatOffset(v: string | number): string {
  const n = typeof v === "number" ? v : parseFloat(v);
  if (isNaN(n)) return String(v);
  const h = Math.floor(n);
  const m = Math.round((n - h) * 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function describeWhen(rule: AppliedRule): string | null {
  const pred = rule.predicate;
  if (!pred) return null;
  const days = pred.days?.length ? ` on ${pred.days.join(", ")}` : "";
  const hi = pred.hoursInterval;

  switch (pred.predicateType) {
    case "ShiftStartOffsetFrom":
      return hi?.start !== undefined
        ? `from ${formatOffset(hi.start)} into shift${days}`
        : null;
    case "ShiftStartOffsetBetween": {
      const hasEnd = hi?.end !== undefined && hi.end !== "" && hi.end !== null;
      if (hi?.start !== undefined && hasEnd)
        return `${formatOffset(hi.start)}–${formatOffset(hi.end!)} into shift${days}`;
      return hi?.start !== undefined
        ? `from ${formatOffset(hi.start)} into shift${days}`
        : null;
    }
    case "TimeOfTheDay":
      if (hi?.start === undefined) return null;
      return hi.end && hi.end !== ""
        ? `${hi.start}–${hi.end}${days}`
        : `from ${hi.start}${days}`;
    default:
      return null;
  }
}

function ruleLabel(rule: AppliedRule): string {
  if (rule.handler === "Percentage")
    return `+${(rule.value / 100).toFixed(2)}%`;
  if (rule.handler === "Addition")
    return `+${(rule.value / 100).toFixed(2)} kr./h`;
  return `${(rule.value / 100).toFixed(2)} kr./h`;
}

// Percentage applies to the base rate (Grundløn), not the running total.
function ruleContributionKr(rule: AppliedRule, baseRateOre: number): number {
  if (rule.handler === "Percentage")
    return (baseRateOre / 100) * (rule.value / 10000);
  if (rule.handler === "Addition")
    return rule.value / 100;
  return 0;
}

function ruleKey(rule: AppliedRule): string {
  return `${rule.identifier}_${rule.handler}_${rule.value}_${rule.predicate?.predicateType ?? "none"}_${rule.predicate?.hoursInterval?.start ?? ""}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const SalaryV2Breakdown: React.FC<Props> = ({ series, total }) => {
  const baseRule = series[0]?.rulesAppied?.find((r) => r.identifier === "Base");
  const baseRateOre = baseRule?.value ?? 0;
  const baseRateKr = baseRateOre / 100;

  // Collect all unique supplement rules across all slots (first-appearance order)
  const allSupplements: AppliedRule[] = [];
  const seen = new Set<string>();
  for (const slot of series) {
    for (const rule of slot.rulesAppied ?? []) {
      if (rule.identifier === "Base") continue;
      const key = ruleKey(rule);
      if (!seen.has(key)) {
        seen.add(key);
        allSupplements.push(rule);
      }
    }
  }

  const colorByKey = new Map<string, number>(
    allSupplements.map((r, i) => [ruleKey(r), i])
  );

  // Group slots by unique hourly rate (preserves chronological order)
  type RateGroup = { rateKr: number; slots: SalaryV2SeriesSlot[] };
  const groups: RateGroup[] = [];
  const rateToGroupIdx = new Map<number, number>();

  for (const slot of series) {
    const rateKr = slot.salary / 100 / (slot.groupLength / 2);
    const mapKey = Math.round(rateKr * 100);
    if (!rateToGroupIdx.has(mapKey)) {
      rateToGroupIdx.set(mapKey, groups.length);
      groups.push({ rateKr, slots: [] });
    }
    groups[rateToGroupIdx.get(mapKey)!].slots.push(slot);
  }

  const groupSlotOffset = groups.map((_, i) =>
    groups.slice(0, i).reduce((s, g) => s + g.slots.length, 0)
  );

  return (
    <div style={{ marginLeft: 16 }}>
      <b data-testid="salary-v2-title">V2 salary</b>

      {/* ── Ingredients ───────────────────────────────────────── */}
      <div style={{ marginTop: 8, marginBottom: 12 }}>
        <div style={{ fontWeight: 600 }}>Ingredients</div>

        <div style={{ marginTop: 2 }}>
          <Chip
            size="small"
            label={`${baseRule?.title ?? "Base salary"}: ${baseRateKr.toFixed(2)} kr./h`}
            style={{ background: "#f5f5f5", color: "#212121", border: "1px solid #bdbdbd", fontWeight: 600 }}
          />
        </div>

        {allSupplements.map((rule, i) => {
          const when = describeWhen(rule);
          const contribution = ruleContributionKr(rule, baseRateOre);
          const ci = colorByKey.get(ruleKey(rule)) ?? i;
          const label = `${rule.title}: ${ruleLabel(rule)}${rule.handler === "Percentage" && contribution > 0 ? ` = ${contribution.toFixed(2)} kr./h` : ""}`;
          const chip = <Chip size="small" label={label} style={chipStyle(ci)} />;
          return (
            <div key={i} style={{ marginTop: 2 }}>
              {when ? <Tooltip title={when} placement="top">{chip}</Tooltip> : chip}
            </div>
          );
        })}
      </div>

      {/* ── Pay per slot ──────────────────────────────────────── */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontWeight: 600 }}>Pay per slot</div>
        {groups.map((group, gi) => {
          const offset = groupSlotOffset[gi];
          return (
            <div key={gi} data-testid={`salary-v2-slot-${gi}`} style={{ marginTop: 6 }}>
              {group.slots.map((slot, si) => (
                <div key={si}>
                  <i className="far fa-clock fa-fw" />{" "}
                  <span data-testid={`salary-v2-slot-range-${offset + si}`}>
                    {format(parseISO(slot.start), "HH:mm")} –{" "}
                    {format(parseISO(slot.end), "HH:mm")}
                  </span>
                </div>
              ))}

              <div style={{ paddingLeft: 24, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
                <span data-testid={`salary-v2-slot-rate-${gi}`}>
                  <strong>{group.rateKr.toFixed(2)} kr./h</strong>
                </span>
                {(() => {
                  const appliedKeys = new Set(
                    group.slots.flatMap(s =>
                      (s.rulesAppied ?? [])
                        .filter(r => r.identifier !== "Base")
                        .map(r => ruleKey(r))
                    )
                  );
                  return allSupplements
                    .filter(rule => appliedKeys.has(ruleKey(rule)))
                    .map((rule) => {
                      const key = ruleKey(rule);
                      const ci = colorByKey.get(key) ?? 0;
                      const when = describeWhen(rule);
                      const chip = <Chip key={key} size="small" label={rule.title} style={chipStyle(ci)} />;
                      return when
                        ? <Tooltip key={key} title={when} placement="top">{chip}</Tooltip>
                        : chip;
                    });
                })()}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Total ─────────────────────────────────────────────── */}
      <span data-testid="salary-v2-total">
        <i className="far fa-dollar-sign fa-fw" /> Total:{" "}
        <strong>{(total / 100).toFixed(2)} kr.</strong>
      </span>
    </div>
  );
};
